<template>
  <Popper
    id="BackgroundProcess" 
    :zIndex="1004" 
    auto-hide 
    placement="bottom-end" 
    :visible.sync="visible"
  >
    <template #trigger="{toggle}">
      <div id="ProcessButton" @click="toggle">
        <div v-if="activeProcessLength" class="active-processes-icon">{{activeProcessLength}}</div>
        <div v-else-if="checkMark" class="check-mark">✔</div>
        <Icon v-if="activeProcessLength || checkMark" icon="fas fa-bell"/>
        <Icon v-else icon="fas fa-bell" style="color: #CFCFCF;"/>
      </div>
    </template>
    <template>
      <div id="ProcessItems">
        <div v-if="!processes" class="process-items-message">{{$t('Loading')}}...</div>
        <div v-else-if="!formattedProcesses.length" class="process-items-message">{{$t('BackgroundProcesses.noRecentProcesses')}}</div>
        <div v-else class="process-items-list">
          <div class="notifications-title">{{$t('Notifications')}}</div>
          <div v-for="(proc, index) of formattedProcesses" :key="index" class="process-item">
            <div class="process-status-icon">
              <Icon v-if="proc.status === 'task-complete'" class="finished-icon" icon="fas fa-check-circle"/>
              <Icon v-else-if="proc.status === 'task-failed'" class="error-icon" icon="fas fa-times-circle"/>
              <Spinner v-else-if="proc.status === 'task-pending'" size="small"/>
              <!-- <Spinner size="small"/> -->
            </div>
            <div class="process-time">
              <Formatter style="font-size: 12px; float: right;" type="dateAgo" :value="getUTCDate(proc.date)"/>
            </div>
            <div class="process-info">
              <div class="process-title">{{proc.title}}</div>
              <template v-if="proc.actions.length">
                <ButtonGroup>
                  <Button v-for="(action, i) of proc.actions" :key="i" type="plain" :disabled="action.disabled" @click="action.onAction">{{action.label}}</Button>
                </ButtonGroup>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Popper>
</template>

<script>

export default {
  name: 'BackgroundProcess',
  data() {
    return {
      visible: false,
      timerHandle: null,
      pinging: false,
      slowTimerHandle: null,
      processes: null,
      checkMark: false
    }
  },
  created() {
    this.eventHub.$on('startBackgroundProcess', () => {
      this.startPing()
    })

    this.checkBackendForProcesses()

  },
  beforeDestroy() {
    this.eventHub.$off('startBackgroundProcess')
    if (this.timerHandle) clearTimeout(this.timerHandle)
    if (this.slowTimerHandle) clearTimeout(this.slowTimerHandle)
  },
  computed: {
    formattedProcesses() {
      if (!this.processes) return []
      // console.log(this.processes)
      return this.processes.map(p => {
        let title = ''
        let actions = []
        if (p.type === 'CAMPAIGN_COPY') {
          title = `${this.typeMap[p.type]}: ${this.getCampaignName(p.data)}`
          actions = [{
            label: this.$t('View'),
            onAction: () => { this.goToCampaignUrl(p.data) },
            disabled: p.data && JSON.parse(p.data).id ? false : true
          }]
        } else if (p.type === 'CONVERSIONS_DOWNLOAD' || p.type === 'EMAILS_DOWNLOAD' || p.type === 'FORM_RESPONSES_DOWNLOAD' || p.type === 'TRANSACTIONS_DOWNLOAD') {
          title = this.typeMap[p.type]
          actions = [{
            label: this.$t('Download'),
            onAction: () => { this.downloadFile(p.data) },
            disabled: p.data && JSON.parse(p.data).url ? false : true
          }]
        }
        return {
          status: p.status,
          date: p.createdOn,
          title,
          description: null,
          actions
        }
      })
      
    },
    activeProcessLength() {
      if (!this.processes) return 0
      const active = this.processes.filter(p => {
        return p.status === 'task-pending'
      })
      return active.length
    },
    typeMap() {
      return {
        'CAMPAIGN_COPY': this.$t('BackgroundProcesses.campaign.copyCampaign'),
        'CONVERSIONS_DOWNLOAD' : this.$t('BackgroundProcesses.conversionsDownload'),
        'EMAILS_DOWNLOAD': this.$t('BackgroundProcesses.emailsDownload'),
        'FORM_RESPONSES_DOWNLOAD': this.$t('BackgroundProcesses.formResponsesDownload'),
        'TRANSACTIONS_DOWNLOAD': this.$t('BackgroundProcesses.transactionsDownload')
      }
    }
  },
  methods: {
    startPing() {
      if (!this.pinging) {
        this.timerHandle = setTimeout(() => {
          this.checkStatus()
          this.visible = true
        }, 200)
      }
    },
    checkStatus() {
      // console.log('checkStatus', Date())
      this.pinging = true
      this.api.pingProcessStatus()
        .then((processes) => {
          // console.log(processes)
          this.checkEmitDataUpdate(this.processes, processes)
          this.checkShowCheckMark(this.processes, processes)
          this.processes = processes
        })
        .catch(this.apiCatch)
        .finally(() => {
          if (this.checkClearInterval()) {
            this.pinging = false
          } else {
            this.timerHandle = setTimeout(this.checkStatus, 2000)
          }
        })
    },
    checkShowCheckMark(oldProcs, newProcs) {
      const prevActiveLength = this.getActiveProcessesLength(oldProcs)
      const currActiveLength = this.getActiveProcessesLength(newProcs)
      if (currActiveLength < prevActiveLength) {
        if (currActiveLength === 0) {
          this.checkMark = true
        }
        this.visible = true
      } else if (currActiveLength >= 1) {
        this.checkMark = false
      }
    },
    checkEmitDataUpdate(oldProcs, newProcs) {
      const oldActiveProcs = this.getActiveProcesses(oldProcs)
      const newActiveProcs = this.getActiveProcesses(newProcs)
      const diffTypes = []
      oldActiveProcs.forEach(oldProc => {
        if (!newActiveProcs.find(newProc => newProc.id === oldProc.id)) {
          diffTypes.push(oldProc.type)
        }
      })
      diffTypes.forEach((t) => {
        if (t === 'CAMPAIGN_COPY') {
          this.eventHub.$emit('updateCampaignData')
        }
      })
    },
    checkBackendForProcesses() {
      this.api.pingProcessStatus()
        .then((processes) => {
          this.processes = processes
          if (this.getActiveProcessesLength(this.processes)) {
            this.startPing()
          }
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.slowTimerHandle = setTimeout(this.checkBackendForProcesses, 5 * 60 * 1000)
        })
    },
    getActiveProcessesLength(processes) {
      const active = this.getActiveProcesses(processes)
      return active.length
    },
    getActiveProcesses(processes) {
      if (!processes) return []
      const active = processes.filter(p => {
        return p.status === 'task-pending'
      })
      return active
    },
    checkClearInterval() {
      for (const proc of this.processes) {
        if (proc.status === 'task-pending') {
          return false
        }
      }
      return true
    },
    getUTCDate(date) {
      return this.theUser && window.moment.tz(date, this.theUser.accountTimezone).tz('UTC').format()
    },
    getCampaignName(strData) {
      if (!strData) return ''
      const camp = JSON.parse(strData)
      return camp && camp.name
    },
    getCampaignUrl(strData) {
      if (!strData) return ''
      const camp = JSON.parse(strData)
      return camp ? `/campaigns/${camp.id}/details` : `/campaigns`
    },
    goToCampaignUrl(strData) {
      const url = this.getCampaignUrl(strData)
      if (url) {
        let routeData = this.$router.resolve(url);
        window.open(routeData.href, '_blank');
      }
    },
    downloadFile(strData) {
      if (!strData) return null
      const data = JSON.parse(strData)
      if (data && data.url) {
        this.visible = false
        window.open(data.url)
      }
    }
  }
}
</script>

<style lang="scss">
#BackgroundProcess {
  #ProcessButton {
    position: relative;
    display: flex;
    width: 40px;
    height: 100%;
    margin-right: 5px;
    margin-top: 3px;
    align-items: center;
    justify-content: center;
    font-size: 1.2em;
    cursor: pointer;
    .active-processes-icon {
      position: absolute;
      z-index: 10;
      width: 12px;
      height: 12px;
      top: -4px;
      right: 10px;
      background-color: #1D73E5;
      border-radius: 8px;
      font-size: 10px;
      text-align: center;
      color: #ffffff;
      line-height: 12px;
    }
    .check-mark {
      position: absolute;
      z-index: 10;
      width: 12px;
      height: 12px;
      top: -4px;
      right: 10px;
      background-color: rgb(3, 182, 3);
      border-radius: 8px;
      font-size: 10px;
      text-align: center;
      color: #ffffff;
      line-height: 12px;
    }

    i {
      width: 20px;
      height: 20px;
      background-size: 20px 20px;
      background-repeat: no-repeat;
      align-items: center;
      justify-content: center;
    }

  }
}
#ProcessItems {
  margin: 4px 0;
  // padding-top: 10px;
  background: #fff;
  border: 1px solid #dbe0e4;
  border-radius: 3px;
  box-shadow: 0 2px 16px rgba(33, 43, 54, 0.08);

  .process-items-message {
    padding: 15px;
  }
  .process-items-list {
    // padding: 10px;
    width: 425px;
    max-height: 150px;
    overflow-y: auto;
    .notifications-title {
      padding-top: 8px;
      padding-bottom: 8px;
      text-align: center;
    }
    .process-item {
      padding: 10px;
      border-top: 2px solid #dbe0e4;
      .process-time {
        width: 90px;
        float: right;
      }
      .process-info {
        padding-left: 30px;
        .process-title {
          font-weight: bold;
          // padding: 5px 0px;
        }
      }
      .process-status-icon {
        float: left;
        .finished-icon {
          padding-left: 5px;
          color: rgb(3, 182, 3);
        }
        .error-icon {
          padding-left: 5px;
          color: red;
        }
      }

    }
    .process-item:first-child {
      border-top: none;
    }
  }
}
</style>